/**
 * PinnedEssentialsBar v3.0 - Responsive Teacher Cockpit Overlay
 * 
 * LAYOUT: 12-column grid with responsive breakpoints
 * - Objectives: col-span-5 (lg) / col-span-7 (md) / col-span-12 (sm)
 * - Agenda: col-span-3 (lg only, hidden below)
 * - Controls: col-span-4 (lg) / col-span-5 (md) / col-span-12 (sm)
 * 
 * Projector-optimized: High contrast, large fonts, no overlap
 */
import React, { useState, useEffect, useRef } from 'react';
import {
    Target, Clock, Play, Pause, RotateCcw,
    Edit3, Check, X, ChevronRight, ChevronUp,
    Eye, EyeOff, Settings, Minimize2, Maximize2, MoreHorizontal
} from 'lucide-react';

// Noise level configuration
const NOISE_LEVELS = [
    { id: 'silent', label: 'Silent', shortLabel: 'Quiet', bg: 'bg-slate-500', icon: '🤫' },
    { id: 'whisper', label: 'Whisper', shortLabel: 'Whisp', bg: 'bg-blue-500', icon: '🤐' },
    { id: 'partner', label: 'Partner', shortLabel: 'Pair', bg: 'bg-violet-500', icon: '👥' },
    { id: 'group', label: 'Group', shortLabel: 'Team', bg: 'bg-amber-500', icon: '👋' }
];

// Format seconds to MM:SS
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Default language objective for student mode
const DEFAULT_LANGUAGE_OBJECTIVE = "I can explain my thinking using academic vocabulary.";

const PinnedEssentialsBar = ({
    // Objectives
    contentObjective = '',
    languageObjective = '',
    onObjectiveChange,

    // Lesson flow for Now/Next
    lessonFlow = [],
    currentSegmentIndex = 0,

    // Timer
    timerSeconds = 300,
    timerPresets = [60, 180, 300, 600],
    onTimerSecondsChange,

    // Noise level
    noiseLevel = 'partner',
    onNoiseLevelChange,

    // Visibility settings
    pinnedSettings = {
        showObjectives: true,
        showTimer: true,
        showNowNext: true,
        showNoise: true
    },
    onPinnedSettingsChange,

    // Mode
    isTeacherMode = false,
    onTeacherModeToggle,

    // Phase 2.3: when true, objectives are visible but not click-to-edit.
    // Timer, noise, collapse, settings remain interactive.
    readOnlyObjectives = false,

    // Phase 2.4: when true, hides setup-only controls (Eye toggle, Settings)
    // from the controls cluster. Collapse remains available.
    isPresenterMode = false,

    // Close callback
    onClose,

    // Optional: callback to advance to next section from the HUD
    onNextSection,
}) => {
    // Collapse state
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Timer state
    const [timerRunning, setTimerRunning] = useState(false);
    const [currentSeconds, setCurrentSeconds] = useState(timerSeconds);
    const [activePreset, setActivePreset] = useState(timerSeconds);
    const timerRef = useRef(null);

    // Preset popover state (for small screens)
    const [showPresetPopover, setShowPresetPopover] = useState(false);

    // Settings popover state
    const [showSettings, setShowSettings] = useState(false);

    // Edit states
    const [editingContent, setEditingContent] = useState(false);
    const [editingLanguage, setEditingLanguage] = useState(false);
    const [tempContentObj, setTempContentObj] = useState(contentObjective);
    const [tempLanguageObj, setTempLanguageObj] = useState(languageObjective);

    // Sync temp state when props change
    useEffect(() => {
        if (!editingContent) setTempContentObj(contentObjective);
    }, [contentObjective, editingContent]);

    useEffect(() => {
        if (!editingLanguage) setTempLanguageObj(languageObjective);
    }, [languageObjective, editingLanguage]);

    // Timer countdown logic
    useEffect(() => {
        if (timerRunning && currentSeconds > 0) {
            timerRef.current = setInterval(() => {
                setCurrentSeconds(prev => {
                    if (prev <= 1) {
                        setTimerRunning(false);
                        onTimerSecondsChange?.(0);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timerRef.current);
    }, [timerRunning, currentSeconds, onTimerSecondsChange]);

    // Sync timer with prop when not running
    useEffect(() => {
        if (!timerRunning) {
            setCurrentSeconds(timerSeconds);
        }
    }, [timerSeconds, timerRunning]);

    // Timer controls
    const toggleTimer = () => {
        if (timerRunning) {
            onTimerSecondsChange?.(currentSeconds);
        }
        setTimerRunning(!timerRunning);
    };

    const resetTimer = () => {
        setTimerRunning(false);
        setCurrentSeconds(activePreset);
        onTimerSecondsChange?.(activePreset);
    };

    const setPreset = (seconds) => {
        setTimerRunning(false);
        setCurrentSeconds(seconds);
        setActivePreset(seconds);
        onTimerSecondsChange?.(seconds);
        setShowPresetPopover(false);
    };

    // Lesson flow with fallback
    const defaultSegments = [
        { title: 'Bell Ringer', duration: 5 },
        { title: 'Direct Instruction', duration: 15 },
        { title: 'Guided Practice', duration: 10 },
        { title: 'Independent Work', duration: 15 },
        { title: 'Exit Ticket', duration: 5 }
    ];
    const effectiveLessonFlow = lessonFlow?.length > 0 ? lessonFlow : defaultSegments;
    const currentSegment = effectiveLessonFlow[currentSegmentIndex] || null;
    const nextSegment = effectiveLessonFlow[currentSegmentIndex + 1] || null;

    // Timer color based on remaining time
    const getTimerColor = () => {
        if (!timerRunning) return 'text-slate-700';
        const ratio = currentSeconds / activePreset;
        if (ratio > 0.5) return 'text-emerald-600';
        if (ratio > 0.25) return 'text-amber-500';
        return 'text-red-500';
    };

    // Timer urgency: pulse ring when last 10% of time
    const isUrgent = timerRunning && (currentSeconds / activePreset) <= 0.1;

    // Save objectives
    const saveContentObjective = () => {
        if (onObjectiveChange) {
            onObjectiveChange('content', tempContentObj);
        }
        setEditingContent(false);
    };

    const saveLanguageObjective = () => {
        if (onObjectiveChange) {
            onObjectiveChange('language', tempLanguageObj);
        }
        setEditingLanguage(false);
    };

    // Handle keyboard in edit mode
    const handleKeyDown = (e, saveFunc, cancelFunc) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveFunc();
        } else if (e.key === 'Escape') {
            cancelFunc();
        }
    };

    // Current noise config
    const currentNoise = NOISE_LEVELS.find(n => n.id === noiseLevel) || NOISE_LEVELS[2];

    // Cycle noise level
    const cycleNoiseLevel = () => {
        if (!isTeacherMode) return;
        const currentIdx = NOISE_LEVELS.findIndex(n => n.id === noiseLevel);
        const nextIdx = (currentIdx + 1) % NOISE_LEVELS.length;
        onNoiseLevelChange?.(NOISE_LEVELS[nextIdx].id);
    };

    // Display objectives (auto-fill language in student mode)
    const displayContentObj = contentObjective || '';
    const displayLanguageObj = languageObjective || (!isTeacherMode ? DEFAULT_LANGUAGE_OBJECTIVE : '');

    // ===== COLLAPSED VIEW =====
    if (isCollapsed) {
        return (
            <div
                className="w-full h-11 flex items-center justify-between px-4 md:px-6 overflow-hidden"
                style={{
                    background: 'rgba(255,255,255,0.92)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}
            >
                {/* Mini Objective Indicator */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="group relative shrink-0">
                        <Target size={18} className="text-emerald-500" />
                        <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-50 w-64 p-3 bg-white rounded-xl shadow-xl border text-sm">
                            <p className="font-semibold text-emerald-600 mb-1">Content</p>
                            <p className="text-slate-700 mb-2">{displayContentObj || 'No objective set'}</p>
                            <p className="font-semibold text-indigo-600 mb-1">Language</p>
                            <p className="text-slate-700">{displayLanguageObj || 'No objective set'}</p>
                        </div>
                    </div>

                    {/* Mini Agenda */}
                    <div className="text-sm font-medium text-slate-600 truncate min-w-0">
                        <span className="text-emerald-600">{currentSegment?.title || '—'}</span>
                        <span className="text-slate-400 ml-2 hidden sm:inline">→ {nextSegment?.title || '—'}</span>
                    </div>
                </div>

                {/* Right: Timer + Noise + Expand */}
                <div className="flex items-center gap-3 shrink-0">
                    <div className={`text-xl font-bold tabular-nums ${getTimerColor()}`}>
                        {formatTime(currentSeconds)}
                    </div>

                    <button
                        onClick={cycleNoiseLevel}
                        className={`h-8 px-2.5 rounded-full ${currentNoise.bg} text-white text-sm font-medium flex items-center gap-1.5 ${isTeacherMode ? 'cursor-pointer hover:opacity-90' : 'cursor-default'} transition-all`}
                    >
                        <span>{currentNoise.icon}</span>
                        <span className="hidden sm:inline">{currentNoise.shortLabel}</span>
                    </button>

                    {isTeacherMode && (
                        <button
                            onClick={() => setIsCollapsed(false)}
                            className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                            title="Expand bar"
                        >
                            <Maximize2 size={16} />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // ===== EXPANDED VIEW =====
    return (
        <div
            className="w-full overflow-hidden"
            style={{
                height: '84px',
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.10)'
            }}
        >
            <div className="h-full max-w-[1920px] mx-auto px-4 md:px-6">
                {/* 12-column grid with responsive columns */}
                <div className="grid grid-cols-12 gap-3 md:gap-4 items-center h-full">

                    {/* ===== LEFT: OBJECTIVES ===== */}
                    {/* lg: 5 cols, md: 5 cols (objective left), sm: full width */}
                    {pinnedSettings.showObjectives && (
                        <div className="col-span-12 md:col-span-5 lg:col-span-5 min-w-0 py-2">
                            {/* Objectives Header - hidden on mobile for space */}
                            <div className="hidden md:flex items-center gap-1.5 mb-1">
                                <Target size={12} className="text-emerald-500 shrink-0" />
                                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                    Objectives
                                </span>
                            </div>

                            {/* Content Objective Row */}
                            <div className="flex items-center gap-2 mb-1 min-w-0">
                                <span className="shrink-0 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                                    CONTENT
                                </span>
                                {editingContent && isTeacherMode && !readOnlyObjectives ? (
                                    <div className="flex-1 flex items-center gap-1 min-w-0">
                                        <input
                                            type="text"
                                            value={tempContentObj}
                                            onChange={(e) => setTempContentObj(e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(e, saveContentObjective, () => setEditingContent(false))}
                                            onBlur={saveContentObjective}
                                            className="flex-1 min-w-0 px-2 py-0.5 text-sm border border-emerald-300 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                                            autoFocus
                                        />
                                        <button onClick={saveContentObjective} className="p-1 text-emerald-600 hover:bg-emerald-100 rounded shrink-0">
                                            <Check size={12} />
                                        </button>
                                        <button onClick={() => setEditingContent(false)} className="p-1 text-slate-400 hover:bg-slate-100 rounded shrink-0">
                                            <X size={12} />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        className={`flex-1 text-sm font-semibold text-slate-800 truncate min-w-0 ${isTeacherMode && !readOnlyObjectives ? 'group cursor-pointer hover:text-emerald-700' : ''}`}
                                        onClick={() => isTeacherMode && !readOnlyObjectives && setEditingContent(true)}
                                        title={displayContentObj || 'Click to set content objective'}
                                    >
                                        {displayContentObj || (isTeacherMode ? (
                                            <span className="text-slate-400 italic text-xs">Click to set...</span>
                                        ) : (
                                            <span className="text-slate-400">—</span>
                                        ))}
                                        {isTeacherMode && !readOnlyObjectives && displayContentObj && (
                                            <Edit3 size={10} className="inline ml-1.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Language Objective Row */}
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="shrink-0 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold">
                                    LANGUAGE
                                </span>
                                {editingLanguage && isTeacherMode && !readOnlyObjectives ? (
                                    <div className="flex-1 flex items-center gap-1 min-w-0">
                                        <input
                                            type="text"
                                            value={tempLanguageObj}
                                            onChange={(e) => setTempLanguageObj(e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(e, saveLanguageObjective, () => setEditingLanguage(false))}
                                            onBlur={saveLanguageObjective}
                                            className="flex-1 min-w-0 px-2 py-0.5 text-sm border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                                            autoFocus
                                        />
                                        <button onClick={saveLanguageObjective} className="p-1 text-indigo-600 hover:bg-indigo-100 rounded shrink-0">
                                            <Check size={12} />
                                        </button>
                                        <button onClick={() => setEditingLanguage(false)} className="p-1 text-slate-400 hover:bg-slate-100 rounded shrink-0">
                                            <X size={12} />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        className={`flex-1 text-sm text-slate-600 truncate min-w-0 ${isTeacherMode && !readOnlyObjectives ? 'group cursor-pointer hover:text-indigo-700' : ''}`}
                                        onClick={() => isTeacherMode && !readOnlyObjectives && setEditingLanguage(true)}
                                        title={displayLanguageObj || 'Click to set language objective'}
                                    >
                                        {displayLanguageObj || (isTeacherMode ? (
                                            <span className="text-slate-400 italic text-xs">Click to set...</span>
                                        ) : (
                                            <span className="text-slate-400">—</span>
                                        ))}
                                        {isTeacherMode && !readOnlyObjectives && displayLanguageObj && (
                                            <Edit3 size={10} className="inline ml-1.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* CENTER: AGENDA — md and up */}
                    {pinnedSettings.showNowNext && (
                        <div className="hidden md:block col-span-12 md:col-span-3 lg:col-span-3 border-l border-slate-200/60 pl-4 min-w-0">
                            <div className="flex items-center gap-1.5 mb-1">
                                <Clock size={12} className="text-blue-500 shrink-0" />
                                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                    Agenda
                                </span>
                            </div>

                            {/* NOW */}
                            <div className="flex items-center gap-2 mb-1 min-w-0">
                                <span className="shrink-0 px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[9px] font-bold uppercase">
                                    Now
                                </span>
                                <span className="text-sm font-semibold text-slate-800 truncate min-w-0">
                                    {currentSegment?.title || '—'}
                                </span>
                            </div>

                            {/* NEXT + quick-advance button */}
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="shrink-0 px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[9px] font-bold uppercase">
                                    Next
                                </span>
                                <ChevronRight size={10} className="text-slate-300 shrink-0" />
                                <span className="text-sm text-slate-500 truncate min-w-0 flex-1">
                                    {nextSegment?.title || '—'}
                                </span>
                                {isTeacherMode && onNextSection && nextSegment && (
                                    <button
                                        onClick={onNextSection}
                                        className="shrink-0 p-1 rounded bg-slate-100 hover:bg-emerald-100 text-slate-400 hover:text-emerald-600 transition-colors"
                                        title="Advance to next section"
                                    >
                                        <ChevronRight size={12} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ===== RIGHT: TIMER + NOISE + CONTROLS ===== */}
                    {/* lg: 4 cols, md: 5 cols, sm: full row below objectives */}
                    <div className="col-span-12 md:col-span-5 lg:col-span-4 flex items-center justify-end gap-2 md:gap-3 shrink-0">

                        {/* Timer Section */}
                        {pinnedSettings.showTimer && (
                            <div className="flex items-center gap-2 shrink-0">
                                {/* Timer Display - urgency ring when <10% */}
                                <div className={`relative rounded-lg px-1 transition-all ${
                                    isUrgent ? 'ring-2 ring-red-400 animate-pulse bg-red-50' : ''
                                }`}>
                                    <div className={`text-2xl md:text-3xl font-bold tabular-nums transition-colors ${getTimerColor()}`}>
                                        {formatTime(currentSeconds)}
                                    </div>
                                </div>

                                {/* Timer Controls - Teacher mode only */}
                                {isTeacherMode && (
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={toggleTimer}
                                            className={`p-1.5 md:p-2 rounded-lg transition-colors ${timerRunning
                                                ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                                                : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                                                }`}
                                            title={timerRunning ? 'Pause' : 'Start'}
                                        >
                                            {timerRunning ? <Pause size={14} /> : <Play size={14} />}
                                        </button>
                                        <button
                                            onClick={resetTimer}
                                            className="p-1.5 md:p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                            title="Reset"
                                        >
                                            <RotateCcw size={14} />
                                        </button>

                                        {/* Presets - visible on xl, popover below */}
                                        <div className="hidden xl:flex bg-slate-100 rounded-lg overflow-hidden">
                                            {timerPresets.map((seconds, idx) => (
                                                <button
                                                    key={seconds}
                                                    onClick={() => setPreset(seconds)}
                                                    className={`text-[10px] px-2 py-1.5 font-medium transition-colors ${activePreset === seconds
                                                        ? 'bg-blue-500 text-white'
                                                        : 'text-slate-600 hover:bg-slate-200'
                                                        } ${idx > 0 ? 'border-l border-slate-200' : ''}`}
                                                >
                                                    {seconds >= 60 ? `${seconds / 60}m` : `${seconds}s`}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Presets popover trigger - below xl */}
                                        <div className="relative xl:hidden">
                                            <button
                                                onClick={() => setShowPresetPopover(!showPresetPopover)}
                                                className="p-1.5 md:p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                                title="Presets"
                                            >
                                                <MoreHorizontal size={14} />
                                            </button>
                                            {showPresetPopover && (
                                                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border p-1 z-50 flex gap-1">
                                                    {timerPresets.map((seconds) => (
                                                        <button
                                                            key={seconds}
                                                            onClick={() => setPreset(seconds)}
                                                            className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${activePreset === seconds
                                                                ? 'bg-blue-500 text-white'
                                                                : 'text-slate-600 hover:bg-slate-100'
                                                                }`}
                                                        >
                                                            {seconds >= 60 ? `${seconds / 60}m` : `${seconds}s`}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Noise Level Pill */}
                        {pinnedSettings.showNoise && (
                            <button
                                onClick={cycleNoiseLevel}
                                className={`h-9 px-3 rounded-full ${currentNoise.bg} text-white font-medium text-sm flex items-center gap-1.5 shadow-md shrink-0 ${isTeacherMode ? 'cursor-pointer hover:opacity-90' : 'cursor-default'} transition-all`}
                            >
                                <span className="text-base">{currentNoise.icon}</span>
                                <span className="hidden sm:inline">{currentNoise.shortLabel}</span>
                            </button>
                        )}

                        {/* Controls Cluster - grouped into one pill */}
                        {/* Phase 2.4: Presenter hides Eye toggle + Settings (setup actions). Collapse stays. */}
                        <div className={`flex items-center gap-0.5 p-1 rounded-xl bg-slate-100/80 shrink-0 ${!isTeacherMode ? 'opacity-40 hover:opacity-100' : ''} transition-opacity`}>
                            {/* Teacher Mode Toggle — hidden in Presenter (redundant with Project button) */}
                            {!isPresenterMode && (
                                <button
                                    onClick={onTeacherModeToggle}
                                    className={`p-1.5 md:p-2 rounded-lg transition-colors ${isTeacherMode
                                        ? 'bg-purple-500 text-white'
                                        : 'text-slate-500 hover:bg-slate-200'
                                        }`}
                                    title={isTeacherMode ? 'Teacher Mode ON' : 'Click for Teacher Mode'}
                                >
                                    {isTeacherMode ? <Eye size={14} /> : <EyeOff size={14} />}
                                </button>
                            )}

                            {/* Collapse Button (Teacher only — available in both Edit and Presenter) */}
                            {isTeacherMode && (
                                <button
                                    onClick={() => setIsCollapsed(true)}
                                    className="p-1.5 md:p-2 rounded-lg text-slate-500 hover:bg-slate-200 transition-colors"
                                    title="Collapse bar"
                                >
                                    <Minimize2 size={14} />
                                </button>
                            )}

                            {/* Settings — Edit mode only (setup action, not live) */}
                            {isTeacherMode && !isPresenterMode && (
                                <div className="relative">
                                    <button
                                        onClick={() => setShowSettings(!showSettings)}
                                        className={`p-1.5 md:p-2 rounded-lg transition-colors ${showSettings ? 'bg-slate-300 text-slate-800' : 'text-slate-500 hover:bg-slate-200'}`}
                                        title="Settings"
                                    >
                                        <Settings size={14} />
                                    </button>
                                    {showSettings && (
                                        <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border p-3 z-50 w-48">
                                            <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Show / Hide</p>
                                            {[
                                                { key: 'showTimer', label: 'Timer' },
                                                { key: 'showObjectives', label: 'Objectives' },
                                                { key: 'showNowNext', label: 'Now / Next' },
                                                { key: 'showNoise', label: 'Noise Level' },
                                            ].map(({ key, label }) => (
                                                <label key={key} className="flex items-center justify-between py-1.5 cursor-pointer group">
                                                    <span className="text-sm text-slate-700 group-hover:text-slate-900">{label}</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={pinnedSettings[key] !== false}
                                                        onChange={() => onPinnedSettingsChange?.({
                                                            ...pinnedSettings,
                                                            [key]: !pinnedSettings[key]
                                                        })}
                                                        className="w-4 h-4 rounded text-purple-500 focus:ring-purple-400"
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PinnedEssentialsBar;
